import { zodResolver } from "@hookform/resolvers/zod/dist/zod"
import { exhaustive } from "exhaustive"
import { isPlainObject } from "lodash"
import { FC, useState } from "react"
import { SubmitHandler, useForm } from "react-hook-form"
import { z } from "zod"
import { useAuth } from "../../Auth/AuthContext"
import { useClient } from "../../Client/ClientAndUserProvider"
import { TrashIcon } from "../../Icons/Icon"
import { API, ServerError } from "../../network/API"
import { FinalizeButton } from "../../Orders/Components/Form/Buttons/Buttons"
import {
	invalidOrganizationNumberMessage,
	invalidPersonNumberMessage,
	organizationNumberRegex,
	personNumberRegex,
} from "../../Orders/Components/Form/constants"
import { ModulePopup } from "../../Orders/Components/ModulePopup/ModulePopup"
import orderConfirmStyle from "../../Orders/Components/OrderConfirmCustomerInformation/OrderConfirmCustomerInformation.module.css"
import { SbtH4 } from "../../Orders/Components/Text/SbtH4/SbtH4"
import { SbtRHFError } from "../../Orders/Components/Text/SbtInvalid/SbtRHFError"
import { PermissionAreaLocation, usePermissions } from "../../PermissionContext"
import { cls } from "../../Shared/cls"
import { GetConsumer } from "../CustomerPortalConsumerInformation/CustomerPortalConsumerInformation"
import editUserStyle from "../EditUser/EditUser.module.css"
import style from "./EditConsumerDetails.module.css"

type EditCustomerDetailsProps = {
	consumer: GetConsumer
	onClose: () => void
	onDone: () => void
}

export const IndividualInfo = z.object({
	name: z.string().min(1),
	personNumber: z.string().regex(personNumberRegex, invalidPersonNumberMessage),
	customerNumber: z.string().optional(),
})

export const OrganizationInfo = z.object({
	companyName: z.string().min(1),
	organizationNumber: z.string().regex(organizationNumberRegex, invalidOrganizationNumberMessage),
	customerNumber: z.string().optional(),
})

export type IndividualInfoType = z.input<typeof IndividualInfo>
export type OrganizationInfoType = z.input<typeof OrganizationInfo>

type UpdateConsumerDetails = {
	name: string
	customerNumber?: string
}

type UpdateConsumerDetailsCompany = {
	type: "Company"
	organizationNumber: string
} & UpdateConsumerDetails

type UpdateConsumerDetailsPrivatePerson = {
	type: "PrivatePerson"
	personNumber: string
} & UpdateConsumerDetails

enum SubmitFailType {
	Generic = "Generic",
	CustomerNumberCollision = "CustomerNumberCollision",
}

export const EditConsumerDetails: FC<EditCustomerDetailsProps> = ({ consumer, onClose: extOnClose, onDone }) => {
	const auth = useAuth()
	const client = useClient()
	const permissions = usePermissions()

	const [isIndividual, setIsIndividual] = useState<boolean>(consumer.consumerDetails.type === "PrivateConsumerDetails")
	const [submitting, setSubmitting] = useState<boolean>(false)
	const [submitFailed, setSubmitFailed] = useState<SubmitFailType | null>(null)

	const {
		register: registerIndividual,
		handleSubmit: handleSubmitIndividual,
		formState: { errors: errorsIndividual, isValid: isValidIndividual },
	} = useForm<IndividualInfoType>({
		mode: "onChange",
		resolver: async (data, context, options) => {
			return zodResolver(IndividualInfo)(data, context, options)
		},
		defaultValues:
			consumer.consumerDetails.type === "PrivateConsumerDetails"
				? {
						name: consumer.consumerDetails.name,
						personNumber: consumer.consumerDetails.personNumber,
						customerNumber: consumer.customerNumber,
				  }
				: undefined,
	})

	const {
		register: registerOrg,
		handleSubmit: handleSubmitOrg,
		formState: { errors: errorsOrg, isValid: isValidOrganization },
	} = useForm<OrganizationInfoType>({
		mode: "onChange",
		resolver: async (data, context, options) => {
			return zodResolver(OrganizationInfo)(data, context, options)
		},
		defaultValues:
			consumer.consumerDetails.type === "CompanyConsumerDetails"
				? {
						companyName: consumer.consumerDetails.name,
						organizationNumber: consumer.consumerDetails.orgNumber,
						customerNumber: consumer.customerNumber,
				  }
				: undefined,
	})

	const onSubmit: SubmitHandler<IndividualInfoType | OrganizationInfoType> = async (data) => {
		if (!permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Information_Update)) {
			return
		}

		setSubmitting(true)
		let req: UpdateConsumerDetailsCompany | UpdateConsumerDetailsPrivatePerson
		if ("personNumber" in data) {
			req = {
				type: "PrivatePerson",
				name: data.name,
				personNumber: data.personNumber,
				customerNumber: data.customerNumber,
			}
		} else {
			req = {
				type: "Company",
				name: data.companyName,
				organizationNumber: data.organizationNumber,
				customerNumber: data.customerNumber,
			}
		}

		API.postWithRetries(`/customer-portal/consumers-v1/${client.identifier}/update/${consumer.id}`, req)
			.then(() => {
				onClose()
				onDone()
			})
			.catch((err) => {
				setSubmitting(false)
				if (err instanceof ServerError) {
					if (err?.data && isPlainObject(err?.data) && "message" in err?.data) {
						const message = err.data.message

						if (message === "customer_number_collision") {
							setSubmitFailed(SubmitFailType.CustomerNumberCollision)
						} else {
							setSubmitFailed(SubmitFailType.Generic)
						}
						return
					}
				}
				setSubmitFailed(SubmitFailType.Generic)
			})
	}

	function onClose() {
		extOnClose()
	}

	function inactivateConsumer() {
		if (!permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Inactivate)) {
			return
		}

		if (window.confirm("Är du säker att du vill inaktivera kunden?")) {
			setSubmitting(true)
			API.deleteWithRetries<void>(`/customer-portal/consumers-v1/${client.identifier}/delete/${consumer.id}`)
				.then(() => {
					onDone()
				})
				.catch(() => {
					setSubmitting(false)
				})
		}
	}

	if (!auth.IsLoggedInClient || !permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Information_Update)) {
		return null
	}

	return (
		<ModulePopup onClose={onClose} className={style.modal} disableClose={submitting}>
			<div className={style.wrapper}>
				<div className={style.header}>Redigera kunduppgifter</div>
				<div style={{ marginTop: "20px" }}>
					<div className={orderConfirmStyle.btnContainer}>
						<label className={orderConfirmStyle.btnIndividualOrCompanySwitch}>
							<input
								type="checkbox"
								name="individualOrCompany"
								id="individualOrCompany"
								value="1"
								defaultChecked={isIndividual}
								onChange={(x) => setIsIndividual(x.target.checked)}
							/>
							<label
								style={{ cursor: "pointer" }}
								htmlFor="individualOrCompany"
								data-on="Privatperson"
								data-off="Företag"
								className={orderConfirmStyle.btnIndividualOrCompanySwitchInner}
							/>
						</label>
					</div>
				</div>
				{isIndividual ? (
					<>
						<form
							style={{ marginTop: "20px" }}
							key="form_individual"
							onSubmit={handleSubmitIndividual(onSubmit)}
							id="form_individual">
							<div className={orderConfirmStyle.fields}>
								<label>
									<SbtH4>Fullständigt namn*</SbtH4>
									<input
										className={orderConfirmStyle.input}
										{...registerIndividual("name")}
										placeholder="Ditt Namn"
									/>
									<SbtRHFError error={errorsIndividual.name} />
								</label>
								<label>
									<SbtH4>Personnummer*</SbtH4>
									<input
										className={orderConfirmStyle.input}
										{...registerIndividual("personNumber")}
										placeholder="19800808-8080"
									/>
									<SbtRHFError error={errorsIndividual.personNumber} />
								</label>
								<label>
									<SbtH4>Kundnummer</SbtH4>
									<input
										className={orderConfirmStyle.input}
										{...registerIndividual("customerNumber")}
										placeholder="ABC123"
									/>
									<SbtRHFError error={errorsIndividual.customerNumber} />
								</label>
							</div>
						</form>
						<p className={orderConfirmStyle.mandatoryFieldInformation}>*Obligatoriskt fält</p>
						{permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Inactivate) ? (
							<div
								className={editUserStyle.deleteUser}
								onClick={() => {
									inactivateConsumer()
								}}>
								<TrashIcon
									size={22}
									iconClassName={cls(editUserStyle.iconColorStroke, editUserStyle.iconColorFill)}
								/>
								<span style={{ lineHeight: "16px" }}>Inaktivera kund</span>
							</div>
						) : null}
						{submitFailed ? (
							<div style={{ color: "var(--invalid-color)", margin: "15px 0 10px 0" }}>
								{exhaustive(submitFailed, {
									Generic: () => "Något gick fel, vänligen försök igen",
									CustomerNumberCollision: () =>
										"Kundnummret är redan satt på en annan kund, vänligen försök igen",
								})}
							</div>
						) : null}
						<FinalizeButton
							className={style.finalize}
							onClick={() => {
								;(document.getElementById("form_individual") as HTMLFormElement).requestSubmit()
							}}
							disabled={!isValidIndividual || submitting}
							type="submit">
							Spara
						</FinalizeButton>
					</>
				) : (
					<>
						<form
							style={{ marginTop: "20px" }}
							key="form_org"
							onSubmit={handleSubmitOrg(onSubmit)}
							id="form_org">
							<div className={orderConfirmStyle.fields}>
								<label>
									<SbtH4>Företagsnamn*</SbtH4>
									<input
										className={orderConfirmStyle.input}
										{...registerOrg("companyName")}
										placeholder="Ditt Företag"
									/>
									<SbtRHFError error={errorsOrg.companyName} />
								</label>
								<label>
									<SbtH4>Organisations&#8203;nummer</SbtH4>
									<input
										className={orderConfirmStyle.input}
										{...registerOrg("organizationNumber")}
										placeholder="555555-5555"
									/>
									<SbtRHFError error={errorsOrg.organizationNumber} />
								</label>
								<label>
									<SbtH4>Kundnummer</SbtH4>
									<input
										className={orderConfirmStyle.input}
										{...registerOrg("customerNumber")}
										placeholder="ABC123"
									/>
									<SbtRHFError error={errorsOrg.customerNumber} />
								</label>
							</div>
						</form>
						<p className={orderConfirmStyle.mandatoryFieldInformation}>*Obligatoriskt fält</p>
						{permissions.isAllowed(PermissionAreaLocation.Client_Consumer_Inactivate) ? (
							<div
								className={editUserStyle.deleteUser}
								onClick={() => {
									inactivateConsumer()
								}}>
								<TrashIcon
									size={22}
									iconClassName={cls(editUserStyle.iconColorStroke, editUserStyle.iconColorFill)}
								/>
								<span style={{ lineHeight: "16px" }}>Inaktivera kund</span>
							</div>
						) : null}
						{submitFailed ? (
							<div style={{ color: "var(--invalid-color)", margin: "15px 0 10px 0" }}>
								{exhaustive(submitFailed, {
									Generic: () => "Något gick fel, vänligen försök igen",
									CustomerNumberCollision: () =>
										"Kundnummret är redan satt på en annan kund, vänligen försök igen",
								})}
							</div>
						) : null}
						<FinalizeButton
							className={style.finalize}
							onClick={() => {
								;(document.getElementById("form_org") as HTMLFormElement).requestSubmit()
							}}
							disabled={!isValidOrganization || submitting}
							type="submit">
							Spara
						</FinalizeButton>
					</>
				)}
			</div>
		</ModulePopup>
	)
}
